// @ts-nocheck
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AddSubscriberModalProps {
  open: boolean;
  onClose: () => void;
  defaultRole?: string;
}

const AddSubscriberModal: React.FC<AddSubscriberModalProps> = ({
  open,
  onClose,
  defaultRole = "athlete"
}) => {
  const [formData, setFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role_slug: defaultRole,
    is_subscribed: true,
    source: "manual" as "manual" | "signup" | "import"
  });

  const queryClient = useQueryClient();

  const addSubscriberMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await (supabase as any)
        .from('communication_subscribers')
        .insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communication-subscribers'] });
      toast.success('Subscriber added successfully');
      onClose();
      setFormData({
        email: "",
        first_name: "",
        last_name: "",
        role_slug: defaultRole,
        is_subscribed: true,
        source: "manual"
      });
    },
    onError: (error) => {
      toast.error('Failed to add subscriber');
      console.error('Add subscriber error:', error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addSubscriberMutation.mutate(formData);
  };

  const roleOptions = [
    { value: "athlete", label: "Athlete" },
    { value: "parent", label: "Parent" },
    { value: "college_coach", label: "College Coach" },
    
    { value: "club_coach", label: "Club Coach" },
    { value: "press", label: "Press" },
    { value: "vendor", label: "Vendor" },
    { value: "admin", label: "Admin" },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Subscriber</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="subscriber@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="John"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role_slug">Role</Label>
            <Select
              value={formData.role_slug}
              onValueChange={(value) => setFormData({ ...formData, role_slug: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="source">Source</Label>
            <Select
              value={formData.source}
              onValueChange={(value: "manual" | "signup" | "import") => 
                setFormData({ ...formData, source: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="signup">Signup</SelectItem>
                <SelectItem value="import">Import</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={addSubscriberMutation.isPending}
            >
              {addSubscriberMutation.isPending ? "Adding..." : "Add Subscriber"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSubscriberModal;
